<template>
  <lila-default-template>
    <template #mainContent>
      <div
        class="container announcements"
        :class="loadingContent ? 'loading large' : ''"
      >
        <h1 class="title">
          Comunique seus Pacientes
        </h1>

        <form
          v-show="!loadingContent"
          class="basic-info-form"
          @submit.prevent="submitForm()"
        >
          <lila-message :alert-data="alert" />

          <div class="columns">
            <div class="column is-5">
              <div class="field-group">
                <div class="field-group-header align-title">
                  <p>Espaço para comunicações de interesse geral</p>
                </div>

                <div class="field-group-body">
                  <lila-input
                    v-model="v.data.title.$model"
                    placeholder="Chamada"
                    :validation="v.data.title"
                  />

                  <lila-textarea
                    v-model="v.data.message.$model"
                    placeholder="Escreva seu comunicado"
                    :validation="v.data.message"
                  />

                  <div class="address-list">
                    <div class="field-group-header align-title">
                      <p>Selecione o grupo de pacientes</p>
                    </div>

                    <team-tree-checklist
                      v-model="data.teamsList"
                      :cover-descendants="true"
                      :tree="teamTree"
                      :is-selectable="isTeamSelectable"
                    />
                  </div>
                </div>
              </div>

              <lila-button
                type="submit"
                title="Enviar"
                class="submit-buttons"
                :loading="loading"
              />
            </div>

            <div class="column is-2 middle-column">
              <div class="line" />
            </div>

            <div class="column is-5">
              <div class="field-group">
                <div class="field-group-header">
                  <p>Lista de Comunicados Enviados</p>
                </div>

                <div class="field-group-body">
                  <div class="announcements-history">
                    <div
                      v-for="item in data.announcementsHistory"
                      :key="item.id"
                      class="history-item"
                    >
                      <p>
                        <time>{{ item.date }}</time>
                        <span>
                          <span
                            v-if="item.title"
                            class="has-text-weight-medium"
                          >{{ item.title }}</span>
                          <br v-if="item.title"> {{ item.message }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";

import Input from "@components/shared/fields/Input.vue";
import Textarea from "@components/shared/fields/Textarea.vue";
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import TeamTreeChecklist from "@components/shared/lila/TeamTreeChecklist.vue";

import { required } from "@vuelidate/validators";

export default {
  components: {
    "lila-input": Input,
    "lila-textarea": Textarea,
    "lila-button": Button,
    "lila-message": Message,
    "team-tree-checklist": TeamTreeChecklist,
    "lila-default-template": DefaultTemplate
  },
  data() {
    return {
      data: {
        title: "",
        message: "",
        announcementsHistory: [],
        teamsList: []
      },
      // Unidades e equipes visíveis (api/Team/available), já escopadas por role no
      // backend e com o Path das unidades ancestrais. Antes esta tela usava
      // professional.Teams do localStorage, gravado no login — a lista ficava
      // defasada até o próximo relogin.
      transferTargets: [],
      loading: false,
      loadingContent: false,
      alert: {
        message: "",
        type: "",
        show: false
      }
    };
  },
  validations() {
    return {
      data: {
        title: {required},
        message: {required}
      }
    };
  },
  computed: {
    // Árvore Unidades › Equipes. Mesma regra do cadastro de profissional:
    // equipe sem Path cai na raiz.
    teamTree() {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.id));
        if (!existing) {
          existing = { id: node.id, name: node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };
      this.transferTargets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit).children;
        });
        findOrAdd(children, target);
      });
      return roots;
    }
  },
  created(){
    this.getAnnouncementsList();
    this.getTeams();
  },
  methods: {
    // Equipes que podem receber o comunicado, escopadas por role no backend
    // e já com as unidades ancestrais (Path) para montar a hierarquia.
    getTeams(){
      this.$axios
        .get(
          "api/Team/available",
          {headers: {"Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}`}}
        )
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
          this.transferTargets = list.filter(Boolean).map((item) => ({
            id: this.$Utilities.getValueIgnoreCase(item, "Id"),
            name: this.$Utilities.getValueIgnoreCase(item, "Name"),
            path: (this.$Utilities.getValueIgnoreCase(item, "Path") || []).map((unit) => ({
              id: this.$Utilities.getValueIgnoreCase(unit, "Id"),
              name: this.$Utilities.getValueIgnoreCase(unit, "Name")
            }))
          }));
        })
        .catch(error => {
          if(error.response) this.$Utilities.verifyToken(error.response.status);
          console.error("Erro ao carregar equipes:", error);
        });
    },
    isTeamSelectable(id) {
      return this.transferTargets.some((target) => String(target.id) === String(id));
    },
    getAnnouncementsList(){
      this.alert.show = false;
      this.loadingContent = true;

    this.$axios
        .get(
          "/api/announcement",
          {headers: {"Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}`}}
        )
        .then((res) => {
          this.data.announcementsHistory = res.data.map((item, index) => {
            return {
              id: index + 1,
              date: this.$Utilities.formatDateWithTime(item.DateAndTime, "YYYY-MM-DDTHH:mm:ss", "DD/MM/YYYY - HH:mm:ss"),
              title: item.Title,
              message: item.Message
            };
          });
        })
        .catch(error => {
          if (error.response) {
            this.$Utilities.verifyToken(error.response.status);
            this.$Utilities.controlAlert(error.response.data, this.alert, "danger");
          } else {
            this.$Utilities.controlAlert(null, this.alert, "danger", "Ocorreu um erro ao carregar os comunicados.");
          }
        })
        .finally(() => this.loadingContent = false);
    },
    submitForm(){
      this.alert.show = false;

      this.v.$touch(); // forçando a validar o fomulário

      if(this.v.$invalid) return;

      if(this.data.teamsList.length == 0){
        this.$Utilities.controlAlert(null, this.alert, "danger", "É necessario informar um grupo de pacientes.");
        return;
      }

      let formData = new FormData();

      formData.append("Title", this.data.title);
      formData.append("Message", this.data.message);

      for(let index in this.data.teamsList){
        formData.append(`Teams[${index}]`, this.data.teamsList[index]);
      }

      this.loading = true;

      this.$axios({
        method: "POST",
        url: "api/announcement",
        data: formData,
        headers: {
          "Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}`,
          "Content-Type": "multipart/form-data"
        }
      })
        .then(res => {
          this.data.announcementsHistory.unshift({
            id: this.data.announcementsHistory.length + 1,
            date: this.$Utilities.formatDateWithTime(res.data.DateAndTime, "YYYY-MM-DDTHH:mm:ss", "DD/MM/YYYY - HH:mm:ss"),
            title: res.data.Title,
            message: res.data.Message
          });

          this.data.title = "";
          this.data.message = "";
          this.data.teamsList = [];

          this.v.$reset();

          this.$Utilities.controlAlert(null, this.alert, "success", "Comunicado enviado com sucesso.");
        })
        .catch(error => {
          this.$Utilities.verifyToken(error.response.status);
          this.$Utilities.controlAlert(error.response.data, this.alert, "danger");
        })
        .finally(() => this.loading = false);
    }
  }
};
</script>

<style>
    @import '~@assets/css/views/announcements.css';
</style>
